/**
 * fundsService.js — API service for Mutual Funds & Indices endpoints.
 * Uses the MSAL-authenticated API client.
 */

import { api } from '../auth/apiClient';

// ───────── Indices ─────────

/** GET /api/funds/indices — all indices summary */
export async function getIndicesSummary() {
    return api.get('/funds/indices');
}

/** GET /api/funds/indices/{symbol}?period=1Y&indexName=... — index chart data */
export async function getIndexDetail(symbol, period = '1Y', indexName = '', options = {}) {
    const params = new URLSearchParams();
    params.set('period', period);
    if (indexName) params.set('indexName', indexName);
    const includeValuation = options?.includeValuation !== false;
    params.set('includeValuation', includeValuation ? 'true' : 'false');
    return api.get(`/funds/indices/${encodeURIComponent(symbol)}?${params.toString()}`, {
        timeout: 180000,
        signal: options?.signal,
    });
}

/** GET /api/funds/benchmarks — list available benchmarks */
export async function getBenchmarksList() {
    return api.get('/funds/benchmarks');
}

// ───────── Mutual Fund Search ─────────

/** GET /api/funds/search?q=... — search mutual fund schemes */
export async function searchFunds(query) {
    return api.get(`/funds/search?q=${encodeURIComponent(query)}`);
}

// ───────── NAV ─────────

/** GET /api/funds/{schemeCode}/nav — full NAV history + meta */
export async function getFundNav(schemeCode) {
    return api.get(`/funds/${schemeCode}/nav`);
}

// ───────── Analytics ─────────

/** GET /api/funds/{schemeCode}/analytics?benchmarkSymbol=...&riskFreeRate=... */
export async function getFundAnalytics(schemeCode, benchmarkSymbol = '', riskFreeRate = 0.07) {
    const params = new URLSearchParams();
    if (benchmarkSymbol) params.set('benchmarkSymbol', benchmarkSymbol);
    if (riskFreeRate !== 0.07) params.set('riskFreeRate', String(riskFreeRate));
    const qs = params.toString();
    return api.get(`/funds/${schemeCode}/analytics${qs ? '?' + qs : ''}`);
}

// ───────── TER ─────────

/** GET /api/funds/{schemeCode}/ter */
export async function getFundTer(schemeCode) {
    return api.get(`/funds/${schemeCode}/ter`);
}

// ───────── Watchlist ─────────

/** GET /api/funds/watchlist */
export async function getWatchlist() {
    return api.get('/funds/watchlist');
}

/** POST /api/funds/watchlist  body: {schemeCode} */
export async function addToWatchlist(schemeCode) {
    return api.post('/funds/watchlist', { schemeCode: Number(schemeCode) });
}

/** DELETE /api/funds/watchlist/{schemeCode} */
export async function removeFromWatchlist(schemeCode) {
    return api.delete(`/funds/watchlist/${schemeCode}`);
}
